import {useNavigate, useParams} from "react-router-dom";
import {Button, Group, Input, Select, Switch, Table, Text, Tooltip, useMantineColorScheme, useMantineTheme} from "@mantine/core";
import {IconCheck, IconEdit, IconPlus, IconTrash, IconX} from "@tabler/icons-react";
import React, {useEffect, useState} from "react";
import {
    FranchiseDetailsQuery,
    useFranchiseDetailsQuery,
    useFranchiseTeamsQuery,
    useSetFranchiseAgmMutation,
    useSetFranchiseGmMutation,
    useUpdateFranchiseMutation,
    useUpdateFranchiseStaffMutation,
    useUserSearchLazyQuery
} from "../../generated/graphql";
import {useDebouncedState} from "@mantine/hooks";
import {Search} from "tabler-icons-react";

interface PendingStaffChangeData {
    GM?: ({ name: string, id: string } | null),
    AGM?: ({ name: string, id: string } | null),
}

export default function FranchiseView() {
    const params = useParams();

    const franchiseQuery = useFranchiseDetailsQuery({variables: {franchiseId: params.franchiseId}});
    const [franchiseData, setFranchiseData] = useState<FranchiseDetailsQuery>();
    const [pendingFranchiseData, setPendingFranchiseData] = useState<any>({});
    const [pendingStaffChanges, setPendingStaffChanges] = useState<PendingStaffChangeData>({});
    const [updateFranchise, {data, loading, error: updateFranchiseError}] = useUpdateFranchiseMutation();
    const [updateStaffing, {
        data: staffData,
        loading: staffLoading,
        error: updateStaffError
    }] = useUpdateFranchiseStaffMutation();
    const [setGm, {data: setGmData, loading: setGmLoading, error: setGmError}] = useSetFranchiseGmMutation();
    const [setAgm, {data: setAgmData, loading: setAgmLoading, error: setAgmError}] = useSetFranchiseAgmMutation();

    useEffect(() => {
        if (franchiseQuery.data) {
            setFranchiseData(franchiseQuery.data);
        }
    }, [franchiseQuery.data]);


    const hasPendingChanges: boolean = checkPendingChanges(franchiseData, pendingFranchiseData, pendingStaffChanges);

    function handleSaveChanges() {
        const tasks: Promise<any>[] = [];
        tasks.push(updateFranchise({
            variables: {
                franchiseId: params.franchiseId,
                name: pendingFranchiseData?.name ?? null,
                active: pendingFranchiseData?.active ?? null,
            }
        }));

        if (pendingStaffChanges.GM) {
            if (pendingStaffChanges.GM.id !== franchiseData?.franchise?.GM?.id) {
                tasks.push(setGm({
                    variables: {
                        userId: pendingStaffChanges.GM.id,
                        franchiseId: params.franchiseId,
                    }
                }));
            }
        }
        if (pendingStaffChanges.AGM) {
            if (pendingStaffChanges.AGM.id !== franchiseData?.franchise?.AGM?.id) {
                tasks.push(setAgm({
                    variables: {
                        userId: pendingStaffChanges.AGM.id,
                        franchiseId: params.franchiseId,
                    }
                }))
            }
        }

        Promise.all(tasks).then(() => {
            setPendingFranchiseData({});
            setPendingStaffChanges({});
            franchiseQuery.refetch().then(() => {
            });
        })
    }

    return <>
        <div className="p-6 space-y-4">
            <h2 className="text-2xl font-bold">
                <EditableString value={pendingFranchiseData?.name ?? franchiseData?.franchise?.name ?? ""}
                                onChange={(value) => {
                                    setPendingFranchiseData({
                                        ...pendingFranchiseData,
                                        name: value
                                    });
                                }}/>
            </h2>

            <Group>
                <EditableUserCard title="General Manager" value={{
                    name: pendingStaffChanges?.GM?.name ?? franchiseData?.franchise?.GM?.name ?? "-",
                    id: pendingStaffChanges?.GM?.id ?? franchiseData?.franchise?.GM?.id ?? "-"
                }}
                                  onChanged={(user) => {
                                      setPendingStaffChanges({
                                          ...pendingStaffChanges,
                                          GM: user
                                      });
                                  }}
                />
                <EditableUserCard title="Ast. General Manager" value={{
                    name: pendingStaffChanges?.AGM?.name ?? franchiseData?.franchise?.AGM?.name ?? "-",
                    id: pendingStaffChanges?.AGM?.id ?? franchiseData?.franchise?.AGM?.id ?? "-"
                }}
                                  onChanged={(user) => {
                                      setPendingStaffChanges({
                                          ...pendingStaffChanges,
                                          AGM: user
                                      });
                                  }}
                />
                <FranchiseActiveCard title="Status" value={
                    pendingFranchiseData?.active ?? franchiseData?.franchise?.active ?? false}
                                     onChanged={(value) => {
                                         setPendingFranchiseData({
                                             ...pendingFranchiseData,
                                             active: value
                                         });
                                     }}/>
            </Group>
            <div>
                {hasPendingChanges ?
                    <Button variant="outline" onClick={handleSaveChanges}>save </Button> : "No pending changes"}
            </div>
            <TeamList franchiseId={params.franchiseId} />
        </div>
    </>
}

function checkPendingChanges(originalData: FranchiseDetailsQuery | undefined, pendingChanges: any, pendingStaff: PendingStaffChangeData): boolean {

    if (!originalData) return false;

    if (pendingChanges.name)
        if (pendingChanges.name !== originalData?.franchise?.name) return true;

    if (pendingStaff?.GM)
        if (pendingStaff?.GM?.id !== originalData?.franchise?.GM?.id) return true;

    if (pendingStaff?.AGM)
        if (pendingStaff?.AGM?.id !== originalData?.franchise?.AGM?.id) return true;

    if (pendingChanges.active !== undefined)
        if (pendingChanges.active !== originalData?.franchise?.active) return true;

    return false;
}

function TeamList({ franchiseId }: { franchiseId?: string }) {
    const navigate = useNavigate();
    const { colorScheme } = useMantineColorScheme();
    const isDark = colorScheme === 'dark';

    const { data, loading, error } = useFranchiseTeamsQuery({
        variables: { franchiseId: franchiseId! },
        skip: !franchiseId,
        fetchPolicy: "cache-and-network",
    });

    const teams = (data?.teams?.items ?? []).map((t) => ({
        id: t.id,
        name: t.name,
        members: t.players?.length ?? 0,
        active: t.active,
    }));

    function handleEditTeam(teamId: string) {
        navigate(`/admin-hub/teams/${teamId}`);
    }

    function handleAddTeam() {
        // Navigate to team create with franchiseId pre-selected via URL param
        navigate(`/admin-hub/teams/create?franchiseId=${franchiseId}`);
    }

    if (loading && teams.length === 0) {
        return <Text size="sm" className="mt-4">Loading teams...</Text>;
    }

    if (error) {
        return <Text size="sm" c="red" className="mt-4">Failed to load teams.</Text>;
    }

    return <>
        <div className="flex justify-between items-center mt-6 mb-2">
            <Text size="lg" fw={600} c={isDark ? "white" : "black"}>Teams</Text>
            <Button
                leftSection={<IconPlus size={16} />}
                size="sm"
                className="bg-blue-500 hover:bg-blue-600"
                onClick={handleAddTeam}
            >
                Add Team
            </Button>
        </div>
        <Table
            striped
            highlightOnHover
            className="shadow-md rounded-lg overflow-hidden text-left"
        >
            <thead className={isDark ? "bg-gray-800" : "bg-gray-100"}>
            <tr>
                <th className="p-3">Team Name</th>
                <th className="p-3">Total Members</th>
                <th className="p-3">Status</th>
                <th className="p-3">Actions</th>
            </tr>
            </thead>
            <tbody>
            {teams.length === 0 ? (
                <tr>
                    <td colSpan={4} className="p-3 text-gray-500">No teams in this franchise.</td>
                </tr>
            ) : (
                teams.map((team) => (
                    <tr className="border-t" key={team.id}>
                        <td className="p-3">{team.name}</td>
                        <td className="p-3">{team.members}</td>
                        <td className="p-3">
                            <span
                                className={`px-2 py-1 rounded-md ${
                                    team.active
                                        ? "bg-green-100 text-green-700"
                                        : "bg-red-100 text-red-700"
                                }`}
                            >
                              {team.active ? "Active" : "Inactive"}
                            </span>
                        </td>
                        <td className="p-3">
                            <Group gap="xs">
                                <Tooltip label="Edit Team">
                                    <Button
                                        size="xs"
                                        variant="filled"
                                        color="blue"
                                        onClick={() => handleEditTeam(team.id)}
                                    >
                                        <IconEdit size={16}/>
                                    </Button>
                                </Tooltip>
                                <Tooltip label="Delete not available">
                                    <Button
                                        size="xs"
                                        variant="light"
                                        color="gray"
                                        disabled
                                    >
                                        <IconTrash size={16}/>
                                    </Button>
                                </Tooltip>
                            </Group>
                        </td>
                    </tr>
                ))
            )}
            </tbody>
        </Table>
    </>
}

interface EditableUserCardProps {
    title: string,
    value: { name: string, id: string },
    onChanged?: (user: ({ name: string, id: string } | null | undefined)) => void
}

// TODO: I hate this and it should be modularized so this component can be resused everyplace that we need to search for and display players
// My thoughts here are that perhaps we can make a generic "user display" that will encapsulate an initial value, and then a toggle for if it is editable
function EditableUserCard({title, value: {name, id}, onChanged}: EditableUserCardProps) {

    const {colorScheme, setColorScheme, clearColorScheme, toggleColorScheme} = useMantineColorScheme();
    const theme = useMantineTheme()
    const isDark = colorScheme === 'dark';
    const [editing, setEditing] = useState(false);

    const [input, setInput] = useDebouncedState('', 200)
    const [userList, setUserList] = useState<{ label: string, value: string }[]>([])
    const [getUsers, {loading, error, data}] = useUserSearchLazyQuery();
    const [newValue, setNewValue] = useState<{ name: string, id: string } | null>(null);

    useEffect(() => {
        getUsers({variables: {input: input}}).then(
            data => {
                setUserList(data.data?.users?.items?.map(x => {
                    return {label: x.name, value: x.id}
                }) ?? []);
            }
        )
    }, [input]);

    if (editing) {
        return (
            <Group
                style={{
                    padding: theme.spacing.md,
                    borderRadius: theme.radius.md,
                    boxShadow: isDark ? 'none' : '0 1px 3px rgba(0, 0, 0, 0.1)',
                    backgroundColor: isDark ? theme.colors.dark[6] : theme.white,
                    border: `1px solid ${isDark ? theme.colors.dark[4] : theme.colors.gray[2]}`,
                    spacing: 0,
                }}
            >
                {/* First Row: Title and Edit Button */}
                <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%'}}>
                    <Text size="xs" color="dimmed">
                        {title}
                    </Text>
                    <div>
                        {/*Confirm update button*/}
                        <Button
                            size="xs"
                            variant="subtle"
                            style={{
                                padding: '2px 4px',
                            }}
                            onClick={() => {
                                if (onChanged) onChanged(newValue);
                                setEditing(false);
                            }}>
                            <IconCheck size={12}/>
                        </Button>
                        {/*Cancel update button*/}
                        <Button
                            size="xs"
                            variant="subtle"
                            style={{
                                padding: '2px 4px',
                            }}
                            onClick={() => {
                                setNewValue(null);
                                setEditing(false);
                            }}>
                            <IconX size={12}/>
                        </Button>
                    </div>
                </div>
                {/* Second Row: Value */}
                <Text size="sm" style={{fontWeight: 500}}>
                    <Select
                        className={`mr-4`}
                        size="md"
                        leftSection={<Search/>}
                        placeholder={name}
                        onSearchChange={(value) => {
                            setInput(value ?? '')
                        }}
                        onChange={(value, item) => {
                            if (item) setNewValue({name: item.label, id: item.value});
                            else setNewValue(null);
                        }}
                        data={userList}
                        searchable
                        maxDropdownHeight={400}
                    />
                </Text>
            </Group>
        );
    } else {
        return (
            <Group
                style={{
                    padding: theme.spacing.md,
                    borderRadius: theme.radius.md,
                    boxShadow: isDark ? 'none' : '0 1px 3px rgba(0, 0, 0, 0.1)',
                    backgroundColor: isDark ? theme.colors.dark[6] : theme.white,
                    border: `1px solid ${isDark ? theme.colors.dark[4] : theme.colors.gray[2]}`,
                    spacing: 0,
                }}
            >
                {/* First Row: Title and Edit Button */}
                <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%'}}>
                    <Text size="xs" color="dimmed">
                        {title}
                    </Text>
                    {onChanged && (
                        <Button
                            size="xs"
                            variant="subtle"
                            onClick={() => {
                                setEditing(true);
                            }}
                            style={{
                                padding: '2px 4px',
                                color: isDark ? theme.colors.gray[3] : theme.colors.gray[7],
                            }}
                        >
                            <IconEdit size={12}/>
                        </Button>
                    )}
                </div>
                {/* Second Row: Value */}
                <Text size="sm" style={{fontWeight: 500}}>
                    {name ?? "-"}
                </Text>
            </Group>
        );
    }
}

function FranchiseActiveCard({title, value, onChanged}: {
    title: string,
    value: boolean,
    onChanged: (value: boolean) => void
}) {

    const {colorScheme, setColorScheme, clearColorScheme, toggleColorScheme} = useMantineColorScheme();
    const theme = useMantineTheme()
    const isDark = colorScheme === 'dark';
    const [editing, setEditing] = useState(false);
    const [newValue, setNewValue] = useState(value);

    if (editing) {
        return (
            <Group
                style={{
                    padding: theme.spacing.md,
                    borderRadius: theme.radius.md,
                    boxShadow: isDark ? 'none' : '0 1px 3px rgba(0, 0, 0, 0.1)',
                    backgroundColor: isDark ? theme.colors.dark[6] : theme.white,
                    border: `1px solid ${isDark ? theme.colors.dark[4] : theme.colors.gray[2]}`,
                    spacing: 0,
                }}
            >
                {/* First Row: Title and Edit Button */}
                <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%'}}>
                    <Text size="xs" color="dimmed">
                        {title}
                    </Text>
                    <div>
                        {/*Confirm update button*/}
                        <Button
                            size="xs"
                            variant="subtle"
                            style={{
                                padding: '2px 4px',
                            }}
                            onClick={() => {
                                onChanged(newValue);
                                setEditing(false);
                            }}>
                            <IconCheck size={12}/>
                        </Button>
                        {/*Cancel update button*/}
                        <Button
                            size="xs"
                            variant="subtle"
                            style={{
                                padding: '2px 4px',
                            }}
                            onClick={() => {
                                setNewValue(value);
                                setEditing(false);
                            }}>
                            <IconX size={12}/>
                        </Button>
                    </div>
                </div>
                {/* Second Row: Value */}
                <Switch
                    checked={newValue}
                    onChange={(val) => {
                        setNewValue(!newValue);
                    }}
                    label={
                        <Text size="sm" style={{fontWeight: 500}}>
                            {newValue ? "Active" : "Inactive"}
                        </Text>
                    }/>
            </Group>
        );
    } else {
        return (
            <Group
                style={{
                    padding: theme.spacing.md,
                    borderRadius: theme.radius.md,
                    boxShadow: isDark ? 'none' : '0 1px 3px rgba(0, 0, 0, 0.1)',
                    backgroundColor: isDark ? theme.colors.dark[6] : theme.white,
                    border: `1px solid ${isDark ? theme.colors.dark[4] : theme.colors.gray[2]}`,
                    spacing: 0,
                }}
            >
                {/* First Row: Title and Edit Button */}
                <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%'}}>
                    <Text size="xs" color="dimmed">
                        {title}
                    </Text>
                    <Button
                        size="xs"
                        variant="subtle"
                        onClick={() => {
                            setEditing(true);
                        }}
                        style={{
                            padding: '2px 4px',
                            color: isDark ? theme.colors.gray[3] : theme.colors.gray[7],
                        }}
                    >
                        <IconEdit size={12}/>
                    </Button>
                </div>
                {/* Second Row: Value */}
                <Text size="sm" style={{fontWeight: 500}}>
                    {value ? "Active" : "Inactive"}
                </Text>
            </Group>
        );
    }
}

function CompactCard({title, value, onEdit}: { title: string, value: string, onEdit?: () => void }) {
    const {colorScheme, setColorScheme, clearColorScheme, toggleColorScheme} = useMantineColorScheme();
    const theme = useMantineTheme()
    const isDark = colorScheme === 'dark';

    return (
        <Group
            style={{
                padding: theme.spacing.md,
                borderRadius: theme.radius.md,
                boxShadow: isDark ? 'none' : '0 1px 3px rgba(0, 0, 0, 0.1)',
                backgroundColor: isDark ? theme.colors.dark[6] : theme.white,
                border: `1px solid ${isDark ? theme.colors.dark[4] : theme.colors.gray[2]}`,
                spacing: 0,
            }}
        >
            {/* First Row: Title and Edit Button */}
            <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%'}}>
                <Text size="xs" color="dimmed">
                    {title}
                </Text>
                {onEdit && (
                    <Button
                        size="xs"
                        variant="subtle"
                        onClick={onEdit}
                        style={{
                            padding: '2px 4px',
                            color: isDark ? theme.colors.gray[3] : theme.colors.gray[7],
                        }}
                    >
                        <IconEdit size={12}/>
                    </Button>
                )}
            </div>
            {/* Second Row: Value */}
            <Text size="sm" style={{fontWeight: 500}}>
                {value}
            </Text>
        </Group>
    );
}

function EditableString({value, onChange}: { value?: string, onChange: (value: string) => void }) {

    const [editing, setEditing] = useState(false);
    const [newValue, setNewValue] = useState(value ?? "");

    if (editing) {
        return <span className="text-2xl font-bold">
            <Input variant="unstyled" size="xs" radius="xs" placeholder={value} value={newValue} onChange={(e) => {
                setNewValue(e.currentTarget.value);
            }}/>
            <Button
                size="xs"
                variant="subtle"
                style={{
                    padding: '2px 4px',
                }}
                onClick={() => {
                    onChange(newValue);
                    setEditing(false);
                }}>
                <IconCheck size={12}/>
            </Button>
            <Button
                size="xs"
                variant="subtle"
                style={{
                    padding: '2px 4px',
                }}
                onClick={() => {
                    setNewValue(value ?? "");
                    setEditing(false);
                }}>
                <IconX size={12}/>
            </Button>
    </span>

    } else {

        return <span className="text-2xl font-bold">
            {value ?? ""}
            <Button
                size="xs"
                variant="subtle"
                style={{
                    padding: '2px 4px',
                }}
                onClick={() => setEditing(true)}
            >
                    <IconEdit size={12}/>
                </Button>
    </span>
    }
}
